import {ellipsis} from 'polished';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {DropdownItemContent} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {Icon, IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {TooltipOverflowCondition} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipOverflowCondition';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {PaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/paletteModel';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {HighlightRangeModel} from '../../../../models/api/conversationHighlightModel';
import {TeamModel} from '../../../../models/api/teamModel';
import {TeamAvatar} from '../../../common/avatars/teamAvatar';
import {Highlight} from '../../cells/header/search/highlight/highlight';

/*
 * Props.
 */

interface AnswersDropdownRowContentProps {
  title: string;
  description: ReactNode;
  iconName: IconName;
  isPrivate: boolean;
  showRightChevron?: boolean;
  showTeamAvatar?: boolean;
  team?: TeamModel;
  highlight?: HighlightRangeModel;
}

/*
 * Style.
 */

interface ContentStyleProps {
  $showRightChevron: boolean;
}

const iconColumnWidth = '28px';
const StyledContentDiv = styled.div<ContentStyleProps>`
  display: grid;
  grid-template-areas:
    'icon title lock-icon chevron'
    'icon description description chevron';
  grid-template-columns: ${iconColumnWidth} auto 1fr ${(p) => (p.$showRightChevron ? iconColumnWidth : '0px')};
`;

const StyledIcon = styled(Icon)`
  grid-area: icon;
  align-self: center;
  justify-self: left;
  margin-top: -1px;
`;

const StyledChevron = styled(Icon)`
  grid-area: chevron;
  align-self: center;
  justify-self: right;
  margin-right: -4px;
  margin-top: -1px;
`;

const StyledTitleDiv = styled.div`
  grid-area: title;
  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.medium};
  ${ellipsis()};
`;

const StyledDescriptionDiv = styled.div`
  grid-area: description;
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.normal};
  margin-top: -1px;
  ${ellipsis()};
`;

const StyledLockIcon = styled(Icon)`
  grid-area: lock-icon;
  margin-left: 6px;
  margin-top: 4px;
`;

const StyledTeamAvatarWrapperDiv = styled.div`
  margin-left: 5px;
  margin-top: 2px;
`;

/*
 * Component.
 */

export const AnswersDropdownRowContent: FC<AnswersDropdownRowContentProps> = (props) => {
  const theme = useUserTheme();

  return (
    <DropdownItemContent>
      <StyledContentDiv $showRightChevron={Boolean(props.showRightChevron)}>
        <StyledIcon name={props.iconName} color={theme.greys.shade70} />

        <StyledTitleDiv>
          <TooltipCoordinator render={() => props.title}>
            <TooltipOverflowCondition>
              <Highlight
                text={props.title}
                ranges={props.highlight ? [props.highlight] : []}
                backgroundColor={PaletteColorsEnum.YELLOW}
              />
            </TooltipOverflowCondition>
          </TooltipCoordinator>
        </StyledTitleDiv>

        {maybeRenderTeamAvatar(props.showTeamAvatar, props.team)}
        {props.isPrivate && <StyledLockIcon name="lockFilledNewTiny" color={theme.greys.shade70} />}

        <StyledDescriptionDiv>{props.description}</StyledDescriptionDiv>

        {props.showRightChevron && <StyledChevron name="chevronRightSmall" color={theme.greys.shade60} />}
      </StyledContentDiv>
    </DropdownItemContent>
  );
};

function maybeRenderTeamAvatar(showTeamAvatar?: boolean, team?: TeamModel) {
  if (!showTeamAvatar || !team) {
    return null;
  }

  return (
    <StyledTeamAvatarWrapperDiv>
      <TeamAvatar team={team} size={VisualSizesEnum.SMALL} />
    </StyledTeamAvatarWrapperDiv>
  );
}
